import React, { Suspense } from "react";
import lazyWithRetry from "./Utils/lazyWithRetry";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "@dr.pogodin/react-helmet";
import { AuthProvider } from "./context/AuthContext";
import { CacheProvider } from "./context/CacheContext";
import { ClientSubscriptionProvider } from "./context/ClientSubscriptionContext";
import { VehicleProvider } from "./context/VehicleContext";

// Initialize i18n for internationalization
import "./i18n";

// Loading fallback for lazy-loaded components
import LoadingFallback from "./components/shared/LoadingFallback";

// Scroll to top on route change
import ScrollToTop from "./components/shared/ScrollToTop";

// Offline status banner (PWA)
import OfflineBanner from "./components/shared/OfflineBanner";

// Global toast provider - overrides window.alert with styled toasts
import GlobalToastProvider from "./components/shared/GlobalToastProvider";

// Global confirm dialog provider - replaces window.confirm with styled modals
import { ConfirmProvider } from "./components/shared/ConfirmDialog";

// ============================================================================
// STATIC IMPORTS - Components needed immediately or very lightweight
// ============================================================================
import Header from "./components/Header";
import Footer from "./components/Footer";
import Home from "./components/Home";
import ProtectedRoute from "./components/ProtectedRoute";
import CookieBanner from "./components/CookieBanner";
import InAppBrowserModal from "./components/shared/InAppBrowserModal";
import IOSInstallPrompt from "./components/shared/IOSInstallPrompt";
import ServiceWorkerUpdateBanner from "./components/shared/ServiceWorkerUpdateBanner";
import PageErrorBoundary from "./components/shared/PageErrorBoundary";
import NotFoundPage from "./components/shared/NotFoundPage";

// ============================================================================
// LAZY IMPORTS - Auth Components (not needed on initial home page load)
// ============================================================================
const Login = lazyWithRetry(() => import("./components/auth/Login"));
const Register = lazyWithRetry(() => import("./components/auth/Register"));
const AuthCallback = lazyWithRetry(() => import("./components/auth/AuthCallback"));
const GoogleCallback = lazyWithRetry(() => import("./components/auth/GoogleCallback"));
const SecuritySettings = lazyWithRetry(() => import("./components/auth/SecuritySettings"));
const ForgotPassword = lazyWithRetry(() => import("./components/auth/ForgotPassword"));
const ResetPassword = lazyWithRetry(() => import("./components/auth/ResetPassword"));
const AdminLogin = lazyWithRetry(() => import("./components/admin/AdminLogin"));

// ============================================================================
// LAZY IMPORTS - Heavy components loaded on-demand
// Reduces initial bundle by ~60-70%
// ============================================================================

// Client Dashboard & Features
const ClientDashboard = lazyWithRetry(() => import("./components/ClientDashboard"));
const MyVehicles = lazyWithRetry(() => import("./components/MyVehicles"));
const AddVehicle = lazyWithRetry(() => import("./components/AddVehicle"));
const ModifyVehicle = lazyWithRetry(() => import("./components/ModifyVehicle"));
const VehicleExpenses = lazyWithRetry(() => import("./components/VehicleExpenses"));
const ProfileEdit = lazyWithRetry(() => import("./components/ProfileEdit"));
const SavedGarages = lazyWithRetry(() => import("./components/SavedGarages"));
const ClientAppointmentsPage = lazyWithRetry(() => import("./pages/ClientAppointmentsPage"));
const AiDiagnosis = lazyWithRetry(() => import("./components/client/AiDiagnosis"));
const DiagnosisDetail = lazyWithRetry(() => import("./components/client/DiagnosisDetail"));

// Mileage Tracking (GPS-based trip tracking)
const MileageSettings = lazyWithRetry(() => import("./components/MileageSettings"));
const MileageTracker = lazyWithRetry(() => import("./components/MileageTracker"));
const MileageHistory = lazyWithRetry(() => import("./components/MileageHistory"));
const TripDetail = lazyWithRetry(() => import("./components/TripDetail"));

// Client Subscription & Pricing
const ClientPricingPage = lazyWithRetry(() => import("./pages/ClientPricingPage"));

// Client Feedback
const FeedbackPage = lazyWithRetry(() => import("./pages/FeedbackPage"));

// SEO: Programmatic city+service pages & blog archive
const CityServicePage = lazyWithRetry(() => import("./pages/CityServicePage"));
const BlogArchive = lazyWithRetry(() => import("./pages/BlogArchive"));

// Garage Dashboard & Core Features
const GarageDashboard = lazyWithRetry(() => import("./components/GarageDashboard"));
const GarageProfile = lazyWithRetry(() => import("./components/GarageProfile"));
const GarageSettings = lazyWithRetry(() => import("./components/GarageSettings"));

// Job Management
const ManageJobs = lazyWithRetry(() => import("./components/ManageJobs"));
const CreateJob = lazyWithRetry(() => import("./components/CreateJob"));
const JobDetailPage = lazyWithRetry(() => import("./pages/JobDetailPage"));
const VehicleManagement = lazyWithRetry(() => import("./components/VehicleManagement"));

// Customer Management
const CustomerDatabase = lazyWithRetry(() => import("./components/CustomerDatabase"));
const AddCustomer = lazyWithRetry(() => import("./components/AddCustomer"));
const CustomerManagement = lazyWithRetry(() => import("./components/CustomerManagement"));
const CustomerProfile = lazyWithRetry(() => import("./components/CustomerProfile"));

// Quote Management
const QuoteManagement = lazyWithRetry(() => import("./components/QuoteManagement"));
const CreateQuote = lazyWithRetry(() => import("./components/CreateQuote"));
const QuoteRequests = lazyWithRetry(() => import("./components/QuoteRequests"));
const PublicEstimateView = lazyWithRetry(() => import("./components/PublicEstimateView"));

// Invoice Management
const InvoiceManagement = lazyWithRetry(() => import("./components/InvoiceManagement"));
const CreateInvoice = lazyWithRetry(() => import("./components/CreateInvoice"));
const InvoiceView = lazyWithRetry(() => import("./components/InvoiceView"));

// Analytics & Reports
const Analytics = lazyWithRetry(() => import("./components/Analytics"));
const TaxReports = lazyWithRetry(() => import("./components/TaxReports"));

// Message Center (heavy - 3000+ lines)
const MessageCenter = lazyWithRetry(() => import("./components/MessageCenter"));
const ConversationView = lazyWithRetry(() => import("./components/ConversationView"));
const RequestQuoteNew = lazyWithRetry(() => import("./components/RequestQuoteNew"));
const BookServiceNew = lazyWithRetry(() => import("./components/BookServiceNew"));

// Search & Compare
const SearchResults = lazyWithRetry(() => import("./components/SearchResults"));
const CompareView = lazyWithRetry(() => import("./components/CompareView"));
const ClaimListing = lazyWithRetry(() => import("./components/ClaimListing"));
const ClaimListingLanding = lazyWithRetry(() => import("./components/ClaimListingLanding"));

// Monetized Features
const PartsAccessories = lazyWithRetry(() => import("./monetized/PartsAccessories"));
const SellMyCar = lazyWithRetry(() => import("./monetized/SellMyCar"));
const InsuranceQuote = lazyWithRetry(() => import("./monetized/InsuranceQuote"));

// Integrations (Accounting Platforms)
const IntegrationsSettings = lazyWithRetry(() => import("./components/booking/IntegrationsSettings"));
const QuickBooksCallback = lazyWithRetry(() => import("./components/booking/QuickBooksCallback"));
const XeroCallback = lazyWithRetry(() => import("./components/booking/XeroCallback"));
const WaveCallback = lazyWithRetry(() => import("./components/booking/WaveCallback"));
const FreshBooksCallback = lazyWithRetry(() => import("./components/booking/FreshBooksCallback"));

// Content Pages
const CarMaintenanceTips = lazyWithRetry(() => import("./components/CarMaintenanceTips"));
const ArticleRouter = lazyWithRetry(() => import("./components/ArticleRouter"));
const ContactUs = lazyWithRetry(() => import("./components/ContactUs"));
const PrivacyPolicy = lazyWithRetry(() => import("./components/PrivacyPolicy"));
const TermsOfService = lazyWithRetry(() => import("./components/TermsOfService"));
const DataDeletionPolicy = lazyWithRetry(() => import("./components/DataDeletionPolicy"));
const HelpCenter = lazyWithRetry(() => import("./components/HelpCenter"));
const MarketingTools = lazyWithRetry(() => import("./components/Marketingtools"));
const GarageContact = lazyWithRetry(() => import("./components/GarageContact"));
const WhyRegister = lazyWithRetry(() => import("./components/WhyRegister"));
const WhyList = lazyWithRetry(() => import("./components/WhyList"));
const AndroidAppPage = lazyWithRetry(() => import("./pages/AndroidAppPage"));

// Admin Dashboard
const AdminDashboard = lazyWithRetry(() => import("./components/admin/AdminDashboard"));
const AdminLandingPagePreview = lazyWithRetry(() => import("./pages/AdminLandingPagePreview"));

// Subscription/Paywall
const PaywallTiers = lazyWithRetry(() => import("./components/PaywallTiers"));

// Phase 5 - Advanced Features
const KanbanBoardPage = lazyWithRetry(() => import("./pages/KanbanBoardPage"));
const ServiceRemindersPage = lazyWithRetry(() => import("./pages/ServiceRemindersPage"));
const JobTemplatesPage = lazyWithRetry(() => import("./pages/JobTemplatesPage"));
const InspectionsPage = lazyWithRetry(() => import("./pages/InspectionsPage"));
const SharedInspectionPage = lazyWithRetry(() => import("./pages/SharedInspectionPage"));
const PublicContentPage = lazyWithRetry(() => import("./pages/PublicContentPage"));
const PartsPlanningPage = lazyWithRetry(() => import("./pages/PartsPlanningPage"));
// Marketing Features
const GaragePromotions = lazyWithRetry(() => import("./components/garage/GaragePromotions"));
const EmailTemplateManager = lazyWithRetry(() => import("./components/garage/EmailTemplateManager"));
const SocialMediaGraphics = lazyWithRetry(() => import("./components/garage/SocialMediaGraphics"));
const ContentLibrary = lazyWithRetry(() => import("./components/garage/ContentLibrary"));

// Native App Components (Mobile-focused interface)
const NativeClientDashboard = lazyWithRetry(() => import("./components/native/NativeClientDashboard"));
const NativeSettingsScreen = lazyWithRetry(() => import("./components/native/NativeSettingsScreen"));
const TripEndScreen = lazyWithRetry(() => import("./components/native/TripEndScreen"));
const WebHandoffPage = lazyWithRetry(() => import("./pages/WebHandoffPage"));
const NativeAppRedirect = lazyWithRetry(() => import("./components/native/NativeAppRedirect"));

// Garage Setup (Claim Approval)
const GarageSetupPage = lazyWithRetry(() => import("./pages/GarageSetupPage"));

// Public Landing Pages
const GarageLandingPageRoute = lazyWithRetry(() => import("./pages/GarageLandingPageRoute"));

// Booking Action (email link handler)
const BookingActionPage = lazyWithRetry(() => import("./pages/BookingActionPage"));

// Garage Review Page (email link and direct access)
const GarageReviewPage = lazyWithRetry(() => import("./pages/GarageReviewPage"));

// Calendar Export Page (ICS download)
const CalendarExportPage = lazyWithRetry(() => import("./pages/CalendarExportPage"));
const EmailPreferencesPage = lazyWithRetry(() => import("./pages/EmailPreferencesPage"));

// Public Booking (from garage landing pages)
const PublicBookingPage = lazyWithRetry(() => import("./pages/PublicBookingPage"));

import "./App.css";

function App() {
  return (
    <GlobalToastProvider>
    <ConfirmProvider>
    <AuthProvider>
      <CacheProvider>
        <ClientSubscriptionProvider>
        <VehicleProvider>
          <HelmetProvider>
            <Router>
          <ScrollToTop />
          <OfflineBanner />
          <div
            style={{
              minHeight: "100vh",
              background: "#fff",
              display: "flex",
              flexDirection: "column",
              fontFamily: "'Poppins', sans-serif",
            }}
          >
            <Header />

            {/* Suspense boundary for lazy-loaded routes */}
            <Suspense fallback={<LoadingFallback />}>
            <PageErrorBoundary>
            <Routes>
              {/* Public Routes - NativeAppRedirect handles native app users */}
              <Route path="/" element={<NativeAppRedirect><Home /></NativeAppRedirect>} />

              {/* New Unified Auth Routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/auth/google/callback" element={<GoogleCallback />} />
              <Route
                path="/security-settings"
                element={
                  <ProtectedRoute>
                    <SecuritySettings />
                  </ProtectedRoute>
                }
              />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />

              {/* Legacy Auth Routes - Redirect to new unified pages */}
              <Route path="/client-login" element={<Navigate to="/login" replace />} />
              <Route path="/garage-login" element={<Navigate to="/claim-listing" replace />} />
              <Route path="/client-register" element={<Navigate to="/register?type=client" replace />} />
              <Route path="/garage-register" element={<Navigate to="/register?type=garage" replace />} />

              {/* Admin Routes - ✅ FIXED: Added userType="admin" protection */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route
                path="/admin/dashboard"
                element={
                  <ProtectedRoute userType="admin">
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/landing-page-preview/:pageId"
                element={
                  <ProtectedRoute userType="admin">
                    <AdminLandingPagePreview />
                  </ProtectedRoute>
                }
              />

              {/* Search and Compare */}
              <Route path="/search-results" element={<SearchResults />} />
              <Route path="/compare-view" element={<CompareView />} />

              {/* Claim Listing Routes */}
              <Route
                path="/claim-listing"
                element={<ClaimListingLanding />}
              />
              <Route
                path="/claim-listing/:garageId"
                element={<ClaimListing />}
              />
              <Route
                path="/garage-setup"
                element={<GarageSetupPage />}
              />

              {/* Maintenance Tips */}
              <Route
                path="/car-maintenance-tips"
                element={<CarMaintenanceTips />}
              />
              <Route path="/article/:slug" element={<ArticleRouter />} />

              {/* Footer Navigation Pages */}
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/data-deletion" element={<DataDeletionPolicy />} />
              <Route path="/help-center" element={<HelpCenter />} />
              <Route path="/marketing-tools" element={<MarketingTools />} />
              <Route path="/garage-contact" element={<GarageContact />} />
              <Route path="/why-register" element={<WhyRegister />} />
              <Route path="/why-list" element={<WhyList />} />
              <Route path="/android-app" element={<AndroidAppPage />} />

              {/* SEO: Programmatic city + service pages */}
              <Route path="/find/:serviceSlug/:citySlug" element={<CityServicePage />} />

              {/* SEO: Blog archive with category filtering */}
              <Route path="/blog" element={<BlogArchive />} />
              <Route path="/blog/:category" element={<BlogArchive />} />

              {/* Public Garage Landing Pages */}
              <Route path="/garage/:slug" element={<GarageLandingPageRoute />} />

              {/* Public Estimate View (email link - no login required) */}
              <Route path="/estimate/:quoteId" element={<PublicEstimateView />} />

              {/* Booking Action (email link handler - public) */}
              <Route path="/booking/action" element={<BookingActionPage />} />

              {/* Garage Review (email link with token or authenticated) */}
              <Route path="/review/:appointmentId" element={<GarageReviewPage />} />

              {/* Calendar Export (ICS download) */}
              <Route path="/calendar/:appointmentId" element={<CalendarExportPage />} />
              <Route path="/email-preferences" element={<EmailPreferencesPage />} />

              {/* Public Booking (from garage landing pages) */}
              <Route path="/book-appointment" element={<PublicBookingPage />} />

              {/* Public Inspection Report (shared link) */}
              <Route path="/inspection/:token" element={<SharedInspectionPage />} />

              {/* Public Content Library item (shared from garage dashboard) */}
              <Route path="/content/:slug" element={<PublicContentPage />} />

              {/* Protected Client Routes */}
              <Route
                path="/client-dashboard"
                element={
                  <ProtectedRoute>
                    <ClientDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/my-vehicles"
                element={
                  <ProtectedRoute>
                    <MyVehicles />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/diagnose"
                element={
                  <ProtectedRoute>
                    <AiDiagnosis />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/diagnosis/:id"
                element={
                  <ProtectedRoute>
                    <DiagnosisDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/add-vehicle"
                element={
                  <ProtectedRoute>
                    <AddVehicle />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/modify-vehicle/:vehicleId"
                element={
                  <ProtectedRoute>
                    <ModifyVehicle />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile-edit"
                element={
                  <ProtectedRoute>
                    <ProfileEdit />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/saved-garages"
                element={
                  <ProtectedRoute>
                    <SavedGarages />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/vehicle-expenses/:vehicleId"
                element={
                  <ProtectedRoute>
                    <VehicleExpenses />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/my-appointments"
                element={
                  <ProtectedRoute>
                    <ClientAppointmentsPage />
                  </ProtectedRoute>
                }
              />

              {/* Mileage Tracking Routes */}
              <Route
                path="/mileage-settings"
                element={
                  <ProtectedRoute>
                    <MileageSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mileage-tracker"
                element={
                  <ProtectedRoute>
                    <MileageTracker />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mileage-history"
                element={
                  <ProtectedRoute>
                    <MileageHistory />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/trip-detail/:tripId"
                element={
                  <ProtectedRoute>
                    <TripDetail />
                  </ProtectedRoute>
                }
              />

              {/* Client Subscription & Pricing */}
              <Route
                path="/client/pricing"
                element={
                  <ProtectedRoute>
                    <ClientPricingPage />
                  </ProtectedRoute>
                }
              />

              {/* Client Feedback */}
              <Route
                path="/feedback"
                element={
                  <ProtectedRoute>
                    <FeedbackPage />
                  </ProtectedRoute>
                }
              />

              {/* Native App Routes (Mobile-focused interface) */}
              <Route
                path="/native-dashboard"
                element={
                  <ProtectedRoute>
                    <NativeClientDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/native-settings"
                element={
                  <ProtectedRoute>
                    <NativeSettingsScreen />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/trip-end"
                element={
                  <ProtectedRoute>
                    <TripEndScreen />
                  </ProtectedRoute>
                }
              />

              {/* Web Handoff - Seamless auth from native app */}
              <Route path="/app-handoff" element={<WebHandoffPage />} />

              {/* Protected Garage Routes */}
              <Route
                path="/garage-dashboard"
                element={
                  <ProtectedRoute userType="garage">
                    <GarageDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/garage-profile"
                element={
                  <ProtectedRoute userType="garage" requiredPermission="settings">
                    <GarageProfile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/garage-settings"
                element={
                  <ProtectedRoute userType="garage" requiredPermission="settings">
                    <GarageSettings />
                  </ProtectedRoute>
                }
              />

              {/* Subscription/Pricing Plans */}
              <Route
                path="/subscription"
                element={
                  <ProtectedRoute>
                    <PaywallTiers />
                  </ProtectedRoute>
                }
              />

              {/* Integrations Routes (QuickBooks) */}
              <Route
                path="/integrations"
                element={
                  <ProtectedRoute userType="garage" requiredPermission="integrations">
                    <IntegrationsSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/quickbooks/callback"
                element={
                  <ProtectedRoute userType="garage">
                    <QuickBooksCallback />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/xero/callback"
                element={
                  <ProtectedRoute userType="garage">
                    <XeroCallback />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/wave/callback"
                element={
                  <ProtectedRoute userType="garage">
                    <WaveCallback />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/freshbooks/callback"
                element={
                  <ProtectedRoute userType="garage">
                    <FreshBooksCallback />
                  </ProtectedRoute>
                }
              />

              {/* Job Management Routes */}
              <Route
                path="/manage-jobs"
                element={
                  <ProtectedRoute userType="garage">
                    <ManageJobs />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/jobs/new"
                element={
                  <ProtectedRoute userType="garage">
                    <CreateJob />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/jobs/:jobId"
                element={
                  <ProtectedRoute userType="garage">
                    <JobDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/kanban"
                element={
                  <ProtectedRoute userType="garage" requiredPermission="job_board">
                    <KanbanBoardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/service-reminders"
                element={
                  <ProtectedRoute userType="garage" requiredPermission="service_reminders">
                    <ServiceRemindersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/job-templates"
                element={
                  <ProtectedRoute userType="garage" requiredPermission="job_templates">
                    <JobTemplatesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/inspections"
                element={
                  <ProtectedRoute userType="garage" requiredPermission="inspections">
                    <InspectionsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/parts-planning"
                element={
                  <ProtectedRoute userType="garage" requiredPermission="parts">
                    <PartsPlanningPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/vehicles"
                element={
                  <ProtectedRoute userType="garage">
                    <VehicleManagement />
                  </ProtectedRoute>
                }
              />

              {/* Customer Management - Updated Routes */}
              <Route
                path="/customers"
                element={
                  <ProtectedRoute userType="garage" requiredPermission="customers">
                    <CustomerDatabase />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/garage-dashboard/manage-customers"
                element={
                  <ProtectedRoute userType="garage" requiredPermission="customers">
                    <CustomerManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/add-customer"
                element={
                  <ProtectedRoute userType="garage" requiredPermission="customers">
                    <AddCustomer />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/customer-profile/:id"
                element={
                  <ProtectedRoute userType="garage" requiredPermission="customers">
                    <CustomerProfile />
                  </ProtectedRoute>
                }
              />

              {/* Message Center Routes (NEW SYSTEM) */}
              <Route
                path="/messages"
                element={
                  <ProtectedRoute>
                    <MessageCenter />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/messages/:conversationId"
                element={
                  <ProtectedRoute>
                    <ConversationView />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/request-quote-new"
                element={
                  <ProtectedRoute>
                    <RequestQuoteNew />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/book-service-new"
                element={
                  <ProtectedRoute>
                    <BookServiceNew />
                  </ProtectedRoute>
                }
              />

              {/* Quote and Invoice Management */}
              <Route
                path="/quotes"
                element={
                  <ProtectedRoute userType="garage" requiredPermission="quotes">
                    <QuoteManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/create-quote/:quoteId?"
                element={
                  <ProtectedRoute userType="garage" requiredPermission="quotes">
                    <CreateQuote />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/quote-requests"
                element={
                  <ProtectedRoute userType="garage" requiredPermission="quotes">
                    <QuoteRequests />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/invoices"
                element={
                  <ProtectedRoute userType="garage" requiredPermission="invoicing">
                    <InvoiceManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/create-invoice"
                element={
                  <ProtectedRoute userType="garage" requiredPermission="invoicing">
                    <CreateInvoice />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/invoice/:invoiceId"
                element={
                  <ProtectedRoute userType="garage" requiredPermission="invoicing">
                    <InvoiceView />
                  </ProtectedRoute>
                }
              />

              {/* Analytics and Reporting */}
              <Route
                path="/analytics"
                element={
                  <ProtectedRoute userType="garage" requiredPermission="analytics">
                    <Analytics />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tax-reports"
                element={
                  <ProtectedRoute userType="garage" requiredPermission="analytics">
                    <TaxReports />
                  </ProtectedRoute>
                }
              />

              {/* Marketing Features */}
              <Route
                path="/promotions"
                element={
                  <ProtectedRoute userType="garage" requiredPermission="promotions">
                    <GaragePromotions />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/email-templates"
                element={
                  <ProtectedRoute userType="garage" requiredPermission="email_templates">
                    <EmailTemplateManager />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/social-media"
                element={
                  <ProtectedRoute userType="garage" requiredPermission="social_media">
                    <SocialMediaGraphics />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/content-library"
                element={
                  <ProtectedRoute userType="garage" requiredPermission="content_library">
                    <ContentLibrary />
                  </ProtectedRoute>
                }
              />

              {/* Monetized Features */}
              <Route path="/parts-accessories" element={<PartsAccessories />} />
              <Route path="/sell-my-car" element={<SellMyCar />} />
              <Route path="/insurance-quote" element={<InsuranceQuote />} />

              {/* Contact */}
              <Route path="/contact" element={<ContactUs />} />

              {/* 404 Route */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            </PageErrorBoundary>
            </Suspense>

            <Footer />
            <CookieBanner />
            <InAppBrowserModal />
            <IOSInstallPrompt />
            <ServiceWorkerUpdateBanner />
          </div>
            </Router>
          </HelmetProvider>
        </VehicleProvider>
        </ClientSubscriptionProvider>
      </CacheProvider>
    </AuthProvider>
    </ConfirmProvider>
    </GlobalToastProvider>
  );
}

export default App;
